import Loading from '../components/loading.html'
import Main from '../main.html'
import BillingAddress from '../components/billing_address.html'
import PaymentInformation from '../components/payment_information.html'
import Payment from '../components/payment.html'
import Summary from '../components/summary.html'
import ApplianceHTML from '../components/appliance.html'
import SubmitButton from '../components/submit_button.html'
import Success from '../components/success.html'
import ErrorHTML from '../components/error.html'
import { htmlElements } from './htmlElements'
import type { Contract, InitOptions } from '../models/sdkModels'
import { CheckoutHandler } from './sdk'
import { AchCodes } from '../../checkout-be/src/schemas/initCheckout'
import { formatPriceDisplay } from './formatPriceDisplay'

export const areScriptsLoaded = (urls: string[]): boolean => {
    const scripts = Array.from(document.getElementsByTagName('script'))
    return urls.some((url) => scripts.some((script) => script.src === url))
}

export const areStylesheetsLoaded = (hrefs: string[]): boolean => {
    const links = Array.from(document.getElementsByTagName('link'))
    return hrefs.some((href) =>
        links.some((link) => link.rel === 'stylesheet' && link.href === href),
    )
}

export const injectCSSandScripts = (): void => {
    const cssUrls: string[] = [
        'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap',
        `${CheckoutHandler.sdkDistributionUrl}/main.min.css`,
    ]

    const scriptUrls: string[] = [
        'https://hosted.paysafe.com/js/v1/latest/paysafe.min.js',
    ]

    const scriptsLoaded = areScriptsLoaded(scriptUrls)
    const cssLoaded = areStylesheetsLoaded(cssUrls)

    if (!cssLoaded) {
        cssUrls.forEach((cssUrl) => {
            const css = document.createElement('link')
            css.rel = 'stylesheet'
            css.href = cssUrl
            document.head.appendChild(css)
        })
    }

    if (!scriptsLoaded) {
        scriptUrls.forEach((scriptUrl) => {
            const scriptTag = document.createElement('script')
            scriptTag.src = scriptUrl
            document.head.appendChild(scriptTag)
        })
    }
}

export function injectLoadingScreen(targetElementId: string): void {
    const getMainTargetElement = document.getElementById(targetElementId)
    if (getMainTargetElement) {
        getMainTargetElement.innerHTML = Loading
    } else {
        console.error(`${targetElementId} Id element not found.`)
    }
}

export function togglePriceDisplayBetweenCreditCardAndACH(
    tabName: 'ach' | 'card',
    checkoutInstance: CheckoutHandler,
): void {
    const basePrices = document.querySelectorAll('.gc-dg-base-price')
    const achPrices = document.querySelectorAll('.gc-dg-ach-price')

    if (tabName === 'card') {
        for (const element of basePrices) {
            element.classList.remove('gc-dg-hidden')
        }
        for (const element of achPrices) {
            element.classList.add('gc-dg-hidden')
        }
    } else {
        for (const element of basePrices) {
            element.classList.add('gc-dg-hidden')
        }
        for (const element of achPrices) {
            element.classList.remove('gc-dg-hidden')
        }
    }

    hideDiscountUIIfNoDiscount(checkoutInstance)
}

export async function injectHTML(
    targetElementId: string,
    checkoutInstance: CheckoutHandler,
): Promise<void> {
    const content = Main
    const getMainTargetElement = document.getElementById(targetElementId)
    if (getMainTargetElement) {
        console.debug('Found target element', targetElementId)
        getMainTargetElement.innerHTML = content

        const billingAddressTargetElement = document.getElementById(
            'gc-dg-billing_address',
        )
        if (billingAddressTargetElement) {
            // Inject customer information into billing address section
            billingAddressTargetElement.innerHTML = injectCustomerInformation(
                BillingAddress,
                checkoutInstance.customer,
            )
        }

        // Inject payment information html skeleton into payment information section
        const paymentInformationTargetElement = document.getElementById(
            'gc-dg-payment_information',
        )
        if (paymentInformationTargetElement) {
            paymentInformationTargetElement.innerHTML = PaymentInformation
        }

        // Inject payment html skeleton into payment section
        const paymentTargetElement = document.getElementById('gc-dg-payment')
        if (paymentTargetElement) {
            paymentTargetElement.innerHTML = Payment
        }

        // Inject submit button html skeleton into submit button section
        const submitButtonTargetElement = document.getElementById(
            'gc-dg-submit-button',
        )
        if (submitButtonTargetElement) {
            submitButtonTargetElement.innerHTML = SubmitButton
        }

        // Insert the summary HTML template into the summary container
        const summaryTargetElement = document.getElementById('gc-dg-summary')
        if (summaryTargetElement) {
            summaryTargetElement.innerHTML = Summary
        }

        console.debug('attempting to render summary')
        injectDataIntoOrderSummary(checkoutInstance)
        console.debug('attempting to render discount')
        handleDiscount(checkoutInstance)
        console.debug('Rendered complete')
    } else {
        console.error(`${targetElementId} Id element not found.`)
    }
}

function hideDiscountUIIfNoDiscount(checkoutInstance: CheckoutHandler): void {
    if (checkoutInstance.achNetFirstPaymentAmountAfterPromotionalPeriod === 0) {
        const showDiscountElements = document.querySelectorAll(
            '.gc-dg-show-discount',
        )
        showDiscountElements.forEach((element: Element) => {
            element.classList.add('gc-dg-hidden')
        })
    }
}

export const handleDiscount = (checkoutInstance: CheckoutHandler): void => {
    let discountAmount: undefined | string
    let contracts: Contract[] = []

    console.info('appliying discount ui')

    checkoutInstance.basket.offers.forEach((offer) => {
        contracts = contracts.concat(offer.contracts)
    })
    const twentyPercentSixMonths = contracts.filter(
        (contract) =>
            contract.contractAttributes.ach.discountProfileAttributes
                ?.achDiscountCodeApplied === 'D20P6M',
    )
    const fiftyPercentSixMonths = contracts.filter(
        (contract) =>
            contract.contractAttributes.ach.discountProfileAttributes
                ?.achDiscountCodeApplied === 'D50P6M',
    )

    const { discountElements, discountDetails } = htmlElements()

    if (twentyPercentSixMonths.length === contracts.length) {
        discountAmount = '20'
    }

    if (fiftyPercentSixMonths.length === contracts.length) {
        discountAmount = '50'
    }

    if (discountAmount) {
        discountElements.forEach((element: Element) => {
            element.innerHTML = discountAmount
            element.classList.remove('gc-dg-hidden')
        })
    } else {
        discountDetails.forEach((element: Element) =>
            element.classList.add('gc-dg-hidden'),
        )
    }

    hideDiscountUIIfNoDiscount(checkoutInstance)
}

// Function to inject and calculate order summary data into the DOM
export const injectDataIntoOrderSummary = (
    checkoutInstance: CheckoutHandler,
): void => {
    // Get references appliances container element
    const { appliancesTargetElement } = htmlElements()

    // Initialize running totals for credit card and ACH payment methods
    let basketTotal = 0
    let achBasketTotal = 0

    // Flatten all contracts from each offer into single array
    const contracts: Contract[] = checkoutInstance.basket.offers.flatMap(
        (offer) => offer.contracts,
    )

    // For each contract/appliance:
    // 1. Add its credit card total to basketTotal
    // 2. Add its ACH total to achBasketTotal
    // 3. Inject its HTML representation into the appliances container
    let ccNetFirstPaymentAmountTotal = 0
    let achNetFirstPaymentAmountTotal = 0
    let achNetFirstPaymentAmountAfterPromotionalPeriod = 0
    for (const appliance of contracts) {
        basketTotal += appliance.contractAttributes.cc.netTotalAmount
        achBasketTotal += appliance.contractAttributes.ach.netTotalAmount
        ccNetFirstPaymentAmountTotal +=
            appliance.contractAttributes.cc.netFirstPaymentAmount
        achNetFirstPaymentAmountTotal +=
            appliance.contractAttributes.ach.netFirstPaymentAmount
        if (appliance.contractAttributes.ach.discountProfileAttributes) {
            achNetFirstPaymentAmountAfterPromotionalPeriod +=
                appliance.contractAttributes.ach.discountProfileAttributes
                    .netFirstPaymentAmountAfterPromotionalPeriod
        }

        // Inject each appliance html template into the appliances container
        appliancesTargetElement.innerHTML +=
            injectPriceAndApplianceDetailsIntoHtml(
                ApplianceHTML,
                appliance,
                appliance.itemDetails.brand,
                appliance.itemDetails.description,
            )
    }

    const netFirstPaymentAmountAfterPromotionalPeriod = document.querySelector(
        '.gc-dg-net-first-payment-amount-after-promotion',
    )
    if (netFirstPaymentAmountAfterPromotionalPeriod) {
        netFirstPaymentAmountAfterPromotionalPeriod.innerHTML =
            formatPriceDisplay(achNetFirstPaymentAmountAfterPromotionalPeriod)
    }

    // Get all elements that should display the credit card total
    const monthlyTotalBasketElements = document.querySelectorAll(
        '.gc-dg-basket-monthly-total',
    )

    // Update all credit card total elements with the calculated total
    monthlyTotalBasketElements.forEach((element: Element) => {
        element.innerHTML = formatPriceDisplay(ccNetFirstPaymentAmountTotal)
        // make all monthly basket totals gc-dg-hidden as ach should be displayed first
        element.classList.add('gc-dg-hidden')
    })

    // Get all elements that should display the ACH total
    const achBasketMonthlyTotalElements = document.querySelectorAll(
        '.gc-dg-ach-basket-monthly-total',
    )

    achBasketMonthlyTotalElements.forEach((element: Element) => {
        element.innerHTML = formatPriceDisplay(achNetFirstPaymentAmountTotal)
    })

    // Store ACH total in checkout instance
    checkoutInstance.achBasketTotal = achBasketTotal

    // Store credit card total in checkout instance
    checkoutInstance.basketTotal = basketTotal

    checkoutInstance.achNetFirstPaymentAmountAfterPromotionalPeriod =
        achNetFirstPaymentAmountAfterPromotionalPeriod
}

/**
 *
 * @description Injects customer information into the HTML template,
 * such as the customer's address, name and phone number.
 */
export const injectCustomerInformation = (
    htmlString: string,
    customer: InitOptions['customer'],
): string => {
    return htmlString
        .replaceAll('{{number}}', customer.address.number as string)
        .replaceAll('{{street}}', customer.address.street)
        .replaceAll('{{postalCode}}', customer.address.postalCode)
        .replaceAll('{{stateCode}}', customer.address.stateCode)
        .replaceAll('{{city}}', customer.address.city)
        .replaceAll('{{customerTitle}}', customer.title)
        .replaceAll('{{customerFirstName}}', customer.firstName)
        .replaceAll('{{customerLastName}}', customer.lastName)
}

export const injectPriceAndApplianceDetailsIntoHtml = (
    htmlString: string,
    appliance: Contract,
    applianceBrand: string,
    applianceDescription: string,
): string => {
    let mutatedHtmlString = htmlString
        .replaceAll(
            '{{netAmount}}',
            formatPriceDisplay(appliance.contractAttributes.cc.netTotalAmount),
        )
        .replaceAll(
            '{{salesTaxAmount}}',
            formatPriceDisplay(appliance.contractAttributes.cc.taxTotalAmount),
        )
        .replaceAll(
            '{{salesTaxRate}}',
            formatPriceDisplay(appliance.contractAttributes.cc.taxRate),
        )
        .replaceAll(
            '{{achNetAmount}}',
            formatPriceDisplay(appliance.contractAttributes.ach.netTotalAmount),
        )
        .replaceAll(
            '{{achSalesTaxAmount}}',
            formatPriceDisplay(appliance.contractAttributes.ach.taxTotalAmount),
        )
        .replaceAll(
            '{{achSalesTaxRate}}',
            formatPriceDisplay(appliance.contractAttributes.ach.taxRate),
        )
        .replaceAll('{{applianceBrand}}', applianceBrand)
        .replaceAll('{{applianceDescription}}', applianceDescription)
        .replaceAll('{{startDate}}', appliance.contractAttributes.effectiveDate)
        // We need to hide the base price elements, as ACH is displayed first
        .replaceAll(/gc-dg-base-price/g, 'gc-dg-base-price gc-dg-hidden')

    const discountProfile =
        appliance.contractAttributes.ach.discountProfileAttributes
    if (discountProfile) {
        // Show ACH discount code percentage if applicable
        const achCodeApplied = discountProfile.achDiscountCodeApplied
        if (achCodeApplied === AchCodes.TwentyPercent) {
            mutatedHtmlString = mutatedHtmlString.replaceAll(
                '{{achDiscountedPercentage}}',
                String('20'),
            )
        }
        if (achCodeApplied === AchCodes.FiftyPercent) {
            mutatedHtmlString = mutatedHtmlString.replaceAll(
                '{{achDiscountedPercentage}}',
                String('50'),
            )
        }
    }

    return mutatedHtmlString
}

export const showSuccessScreen = (checkoutInstance: CheckoutHandler): void => {
    const targetElement = document.getElementById(
        checkoutInstance.targetElementId,
    ) as HTMLElement

    targetElement.innerHTML = Success
}

export const showErrorScreen = (checkoutInstance: CheckoutHandler): void => {
    const targetElement = document.getElementById(
        checkoutInstance.targetElementId,
    ) as HTMLElement

    targetElement.innerHTML = ErrorHTML
}
